import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { OrganizationService } from 'src/app/@shared';
import { environment } from 'src/environments/environment';
import {
  DataFeedControlResult,
  DataFeedIssuePage,
  DataFeedIssueQuery,
  DataFeedQuery,
  DataFeedTrace,
  DataFeedUploadHistoryDetail,
  DataFeedView,
  ETLIACopyStatus,
  ImportUploadActionResult,
  ImportUploadPreview,
  ImportUploadResponse,
  ProcessedFile,
  QueuedFiles,
} from '../models/data-feed.model';

@Injectable({
  providedIn: 'root',
})

export class EtlMonitorService {
  constructor(
    private http: HttpClient,
    private organizationService: OrganizationService
  ) {}

  getEtlFileStatus(): Observable<any> {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/ETLDashboard/GetETLFileStatus`;
    return this.http.get<ProcessedFile[]>(url);
  }

  GetETLInQueueFileStatus(): Observable<any> {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/ETLDashboard/GetETLInQueueFileStatus`;
    return this.http.get<QueuedFiles[]>(url);
  }

  getEtlFileStatusForDataFeed(): Observable<ProcessedFile[]> {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/ETLDashboard/GetETLFileStatus`;
    return this.http.get<ProcessedFile[]>(url);
  }

  GetETLInQueueFileStatusForDataFeed(): Observable<QueuedFiles[]> {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/ETLDashboard/GetETLInQueueFileStatus`;
    return this.http.get<QueuedFiles[]>(url);
  }

  getIACopyStatus(runId: string): Observable<ETLIACopyStatus | null> {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/ETLDashboard/GetIACopyStatus?runId=${encodeURIComponent(runId)}`;
    return this.http.get<ETLIACopyStatus | null>(url);
  }

  /**
   * Stages an import zip and returns the preview of what its manifest will do. Nothing is queued until
   * confirmUpload(). A zip without a readable manifest.json is rejected (400; the body's message says why).
   */
  uploadFile(file: File): Observable<ImportUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<ImportUploadResponse>(`${this.etlDashboardUrl}/UploadFile`, formData);
  }

  /** The preview of a staged upload; 404 once it was confirmed, discarded or expired. */
  getUploadPreview(uploadId: string): Observable<ImportUploadPreview> {
    const params = new HttpParams().set('uploadId', uploadId);
    return this.http.get<ImportUploadPreview>(`${this.etlDashboardUrl}/GetUploadPreview`, { params });
  }

  /** Moves the staged zip into the import folder, which queues the import. */
  confirmUpload(uploadId: string): Observable<ImportUploadActionResult> {
    const params = new HttpParams().set('uploadId', uploadId);
    return this.http.post<ImportUploadActionResult>(`${this.etlDashboardUrl}/ConfirmUpload`, null, { params });
  }

  /** One logged upload with its manifest, the preview shown at confirmation and the linked run. */
  getUploadHistoryEntry(uploadId: string): Observable<DataFeedUploadHistoryDetail> {
    const params = new HttpParams().set('uploadId', uploadId);
    return this.http.get<DataFeedUploadHistoryDetail>(`${this.etlDashboardUrl}/GetUploadHistoryEntry`, { params });
  }

  /** The archived zip of a confirmed upload (ETLDashboard/DownloadUploadPackage). */
  downloadUploadPackage(uploadId: string): Observable<HttpResponse<Blob>> {
    const params = new HttpParams().set('uploadId', uploadId);
    return this.http.get(`${this.etlDashboardUrl}/DownloadUploadPackage`, { params, responseType: 'blob', observe: 'response' });
  }

  /** Deletes the staged zip; nothing is imported. */
  discardUpload(uploadId: string): Observable<ImportUploadActionResult> {
    const params = new HttpParams().set('uploadId', uploadId);
    return this.http.post<ImportUploadActionResult>(`${this.etlDashboardUrl}/DiscardUpload`, null, { params });
  }
  /** Base URL of the ETL dashboard controller for the current organization. */
  private get etlDashboardUrl(): string {
    return `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/ETLDashboard`;
  }

  /**
   * Live data feed: one page of the uploads in the window with their stage, progress and per-entity status, filtered by
   * stage group and search on the server. The summary covers the whole window.
   */
  getDataFeed(query: DataFeedQuery): Observable<DataFeedView> {
    let params = new HttpParams().set('hours', String(query.hours));
    if (query.stage && query.stage !== 'all') { params = params.set('stage', query.stage); }
    const search = query.search?.trim();
    if (search) { params = params.set('search', search); }
    if (query.pageSize) {
      params = params.set('page', String(query.page ?? 1)).set('pageSize', String(query.pageSize));
    }
    return this.http.get<DataFeedView>(`${this.etlDashboardUrl}/GetDataFeed`, { params });
  }

  /** Full trace of one run: upload, events, IA steps, dead letters and issue counts. */
  getDataFeedTrace(runId: string): Observable<DataFeedTrace> {
    const params = new HttpParams().set('runId', runId);
    return this.http.get<DataFeedTrace>(`${this.etlDashboardUrl}/GetDataFeedTrace`, { params });
  }

  /** One page of record/validation issues for a run, optionally filtered by entity and kind. */
  getDataFeedIssues(runId: string, query: DataFeedIssueQuery = {}): Observable<DataFeedIssuePage> {
    let params = new HttpParams()
      .set('runId', runId)
      .set('page', String(query.page ?? 1))
      .set('pageSize', String(query.pageSize ?? 50));
    if (query.entity) {
      params = params.set('entity', query.entity);
    }
    if (query.kind) {
      params = params.set('kind', query.kind);
    }
    return this.http.get<DataFeedIssuePage>(`${this.etlDashboardUrl}/GetDataFeedIssues`, { params });
  }

  /** Asks the import worker to pause a queued or running import; it can be started again later (POST, runId in the query string). */
  pauseDataFeedRun(runId: string): Observable<DataFeedControlResult> {
    const params = new HttpParams().set('runId', runId);
    return this.http.post<DataFeedControlResult>(`${this.etlDashboardUrl}/PauseDataFeedRun`, null, { params });
  }

  /** Asks the import worker to cancel an import for good (POST, runId in the query string). */
  cancelDataFeedRun(runId: string): Observable<DataFeedControlResult> {
    const params = new HttpParams().set('runId', runId);
    return this.http.post<DataFeedControlResult>(`${this.etlDashboardUrl}/CancelDataFeedRun`, null, { params });
  }

  /** Asks the import worker to start a paused import again from where it stopped (POST, runId in the query string). */
  resumeDataFeedRun(runId: string): Observable<DataFeedControlResult> {
    const params = new HttpParams().set('runId', runId);
    return this.http.post<DataFeedControlResult>(`${this.etlDashboardUrl}/ResumeDataFeedRun`, null, { params });
  }
}
